import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "@/components/ui/tooltip";
import { Copy, Check } from "lucide-react";

interface EnhancedCopyButtonProps {
  textToCopy: string;
  size?: "sm" | "default" | "lg" | "icon";
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  className?: string;
}

export const EnhancedCopyButton: React.FC<EnhancedCopyButtonProps> = ({
  textToCopy,
  size = "sm",
  variant = "outline",
  className = ""
}) => {
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopyState("copied");
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  useEffect(() => {
    if (copyState === "copied") {
      const timer = setTimeout(() => {
        setCopyState("idle");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [copyState]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size={size}
            variant={variant}
            onClick={handleCopy}
            className={className}
          >
            {copyState === "copied" ? (
              <Check className="w-4 h-4 text-purple-600" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{copyState === "copied" ? "Copied!" : "Copy"}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};